import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CircleAlert, CircleCheck, Loader2 } from "lucide-react";
import AppShell from "@/components/layout/AppShell";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { Card } from "@/components/settings/parts";
import ModePicker from "@/components/hosting/ModePicker";
import EmulatorPanel from "@/components/hosting/EmulatorPanel";
import CloudflareFields from "@/components/hosting/CloudflareFields";
import TokenField from "@/components/hosting/TokenField";
import SetupSteps from "@/components/hosting/SetupSteps";
import CheckPanel from "@/components/hosting/CheckPanel";
import ClientDomainsCard from "@/components/hosting/ClientDomainsCard";
import AddressScheme, { DnsRecords } from "@/components/hosting/AddressScheme";
import { errMsg, useApi } from "@/lib/api";

function StatusBanner({ h }) {
  const label = h.mode_labels[h.mode];
  const [ok, text] = h.mode === "internal" ? [true, "I siti compilati sono serviti dallo Studio (noindex). Scegli l'emulatore per provare l'hosting esterno senza costi."]
    : h.active ? [true, `Attivo: le nuove compilazioni dei siti attivati vengono pubblicate su «${label}». Siti attivati: ${h.sites_enabled} · con indirizzi pubblicati: ${h.sites_on_edge}. Si attiva sito per sito in Siti › sito › Impostazioni.`]
      : [false, `Configurazione di «${label}» incompleta: finché non completi i passaggi, le nuove compilazioni restano sul server interno.`];
  return (
    <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-sm ${ok ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-amber-200 bg-amber-50 text-amber-900"}`} data-testid="hosting-status-banner">
      {ok ? <CircleCheck size={18} className="mt-0.5 shrink-0 text-emerald-600" /> : <CircleAlert size={18} className="mt-0.5 shrink-0 text-amber-600" />}<p>{text}</p>
    </div>
  );
}

const CF_GUIDE = ["Registra il dominio dedicato con Cloudflare Registrar (è subito attivo, senza cambiare nameserver) o aggiungilo al piano Free.",
  "Attiva R2 dalla dashboard (richiede un metodo di pagamento; 10 GB al mese gratuiti).", "Crea un token API con i permessi elencati qui sotto e incollalo nel campo dedicato.",
  "Copia Account ID e Zone ID, salvali, poi esegui i passaggi uno alla volta.", "Crea il record DNS indicato e premi «Verifica configurazione»."];

export default function Hosting() {
  const api = useApi();
  const [h, setH] = useState(null);
  const [pick, setPick] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/hosting"); setH(data); } catch (e) { toast.error(errMsg(e)); setH(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);
  const switchMode = async () => {
    const mode = pick;
    setPick(null);
    try { const { data } = await api.put("/hosting", { mode, ...h.cloudflare, preview_suffix: h.preview_suffix, live_suffix: h.live_suffix }); setH(data); toast.success(`Hosting: ${data.mode_labels[mode]}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!h) return <AppShell><div className="flex items-center gap-2 p-12 text-sm text-slate-500">{h === false ? "Impostazioni non disponibili" : <><Loader2 size={14} className="animate-spin" />Caricamento…</>}</div></AppShell>;
  const edge = h.mode !== "internal";
  return (
    <AppShell>
      <div className="max-w-5xl space-y-6 px-5 py-8 sm:px-8 lg:px-12 lg:py-12" data-testid="hosting-page">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Hosting dei siti</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-500">Dove vengono pubblicati i siti compilati. Con Cloudflare (o l'emulatore) ogni sito ha il suo indirizzo e resta online anche se lo Studio è spento.</p>
        </div>
        <StatusBanner h={h} />
        <ModePicker h={h} onPick={setPick} />
        {h.mode === "emulator" && <Card title="Emulatore locale" desc="Worker, R2 e KV di Cloudflare simulati da Miniflare su questo server." testid="hosting-card-emulator"><EmulatorPanel h={h} reload={load} onChange={setH} /></Card>}
        {h.mode === "cloudflare" && (
          <Card title="Account Cloudflare" desc="Nessuna risorsa viene creata finché non esegui e confermi i passaggi qui sotto." testid="hosting-card-cloudflare">
            <ol className="list-decimal space-y-1 pl-5 text-xs text-slate-600" data-testid="hosting-cf-guide">{CF_GUIDE.map((g) => <li key={g}>{g}</li>)}</ol>
            <CloudflareFields h={h} onChange={setH} />
            <div className="border-t border-slate-100 pt-4"><p className="mb-2 text-sm font-medium text-slate-900">Token API</p><TokenField h={h} onChange={setH} /></div>
          </Card>
        )}
        {edge && h.derived && (
          <Card title="Indirizzi dei siti" desc="Schema per ogni sito (es. identificativo «trattoria»). Con un dominio del cliente attivo, l'indirizzo online rimanda lì con un 301." testid="hosting-card-addresses">
            <AddressScheme d={h.derived} />
            {h.mode === "cloudflare" && <><p className="text-sm font-medium text-slate-900">Record DNS da creare su Cloudflare</p><DnsRecords d={h.derived} /></>}
          </Card>
        )}
        {edge && <Card title="Configurazione" desc="Ogni passaggio crea o aggiorna una sola risorsa e chiede conferma. Si possono ripetere senza effetti collaterali." testid="hosting-card-steps"><SetupSteps h={h} onChange={setH} /></Card>}
        {edge && <Card title="Verifica e sincronizzazione" testid="hosting-card-check"><CheckPanel h={h} reload={load} /></Card>}
        {edge && <Card title="Domini dei clienti" desc="Cloudflare for SaaS: i siti sui domini dei clienti (es. www.cliente.it) con HTTPS automatico. Una sola configurazione per tutti i siti." testid="hosting-card-domains"><ClientDomainsCard mode={h.mode} /></Card>}
      </div>
      <ConfirmDialog open={!!pick} onOpenChange={(o) => !o && setPick(null)} onConfirm={switchMode} confirmLabel="Cambia hosting"
        title={pick ? `Pubblicare i siti su «${h.mode_labels[pick]}»?` : ""}
        description="Vale per le prossime compilazioni. Le versioni già pubblicate restano dove sono: per spostare un sito sul nuovo hosting ricompilalo e pubblicalo di nuovo." />
    </AppShell>
  );
}
